import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Navbar, ScreenActivity, ChapterSideBar, CircleFlowBar, CircleFlowSideBar } from "@/components";
import { Toolkit } from "@/components/Toolkit/Toolkit";
import { useTranslation } from "react-i18next";
import { FS } from "@/singletons/FileSystem";
import { ErrorPage } from "./";


import {
  Communication,
  NotificationProvider,
  useEditorState,
} from "@/store";
import {
  handleRidersLogin,
  InitializationError,
  isPublicProject,
  socket as NotificationSocket,
  sentryRegisterStateBreadcrumb,
  SessionError
} from "@/utils";

import AlertArea from "@/components/AlertArea";
import SessionExpiredAlert from "@/components/SessionExpiredAlert";
import ConnectionAlertArea from "@/components/ConnectionAlertArea";
import sessionSocket from "@/singletons/SessionSocket";
import Layout from "@/components/Layout";
import LayoutLoadingOverlay from "@/components/LayoutLoadingOverlay";
import * as Sentry from "@sentry/react";

// Safety cutoff for the Godot boot overlay: if godotLoadingComplete never
// arrives (e.g. a Godot-side bug) this keeps a broken build from hard-
// blocking the editor behind the loading screen forever.
const GODOT_LOADING_TIMEOUT_MS = 10000 * 60 * 5;

export const Editor: React.FC<{ isTwa?: boolean }> = ({ isTwa }) => {
  const {
    init: initEditorState,
    setGodotContainerRef,
    initialized,
    sessionError,
    setSessionError,
    metadata,
    backwardCompatibility,
    godotVersionedBuild,
    godotLoadingComplete,
  } = useEditorState();

  const { t } = useTranslation();
  const { token } = useParams();
  const godotContainerRef = useRef<HTMLIFrameElement | null>(null);
  const [godotLoadingTimedOut, setGodotLoadingTimedOut] = useState(false);

  useEffect(() => {
    if (!godotVersionedBuild || godotLoadingComplete) return;

    const timeout = setTimeout(() => {
      setGodotLoadingTimedOut(true);
      Sentry.captureMessage("godotLoadingComplete not received within timeout", "warning");
    }, GODOT_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [godotVersionedBuild, godotLoadingComplete]);

  const showGodotBootOverlay = godotVersionedBuild && !godotLoadingComplete && !godotLoadingTimedOut;

  useLayoutEffect(() => {
    if (initialized && !showGodotBootOverlay) document.getElementById("boot-loading")?.remove();
  }, [initialized, showGodotBootOverlay]);

  useEffect(() => {
    async function initEditor() {
      await handleRidersLogin();
      await initEditorState(token!);
      await FS.init();
    };

    setGodotContainerRef(godotContainerRef);
    sentryRegisterStateBreadcrumb(useEditorState);

    initEditor()
      .then(() => {
        if (
          useEditorState.getState().environment !== "local"
          && !isPublicProject()
          && useEditorState.getState().platform !== "lti"
        ) {
          sessionSocket.connect();
          NotificationSocket(metadata?.session_id || "");
        }

        useEditorState.setState({ initialized: true });
      })
      .catch((err) => {
        if (err instanceof SessionError) {
          setSessionError(err.message);
        }
        Sentry.captureException(new InitializationError("Failed to initialize editor", err as Error));
      });

    return () => {
      sessionSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE === "stembox") return;
    console.log("[APP MODE] ", isTwa ? "TWA" : "NORMAL WEB APP");
    console.log("[LOCATION] ", window.location);
  }, [isTwa]);

  if (sessionError) {
    return (
      <Toolkit>
        <ErrorPage message={t(sessionError)} />
      </Toolkit>
    )
  }

  if (!initialized) {
    return (
      <div>
      </div>
    )
  }

  // The Navbar is riders.ai's own chrome, so it belongs only where the editor
  // is the whole page in a browser tab. LTI renders inside someone else's LMS,
  // the TWA shell draws its own, and the desktop shell is an app window whose
  // chrome is the OS title bar.
  //
  // Note which check catches the desktop: platform === "electron", not
  // VITE_ENV. The shell loads the deployed production build, so VITE_ENV reads
  // "production" there and the flag below only ever fires for the separate
  // electron-mode bundle. Both are kept -- they cover different builds.
  const platform = useEditorState.getState().platform;
  const showNavbar =
    import.meta.env.VITE_ENV !== "electron"
    && !isTwa
    && platform !== "lti"
    && platform !== "electron";

  return (
    <Toolkit>
      <NotificationProvider>
        <div className="w-full h-screen relative flex space-x-2 py-4 bg-background">
          {/* env */}
          {showNavbar && <Navbar />}
          {backwardCompatibility && <ChapterSideBar />}
          <CircleFlowSideBar />
          <div className="w-full h-full flex flex-col space-y-2 mr-2">
            {/* Don't display it on local */}
            {import.meta.env.MODE !== "stembox" && !isPublicProject() && <AlertArea />}

            {/* Don't display it anywhere except staging */}
            {/* {(import.meta.env.MODE === "staging") && <ConnectionAlertArea />} */}

            <CircleFlowBar />

            <div className="relative w-full h-full">
              <Communication>
                <Layout />
              </Communication>
              <LayoutLoadingOverlay />
            </div>
          </div>
          <ScreenActivity />
          {!isPublicProject() && <SessionExpiredAlert />}

        </div>
      </NotificationProvider>
    </Toolkit>
  );
};
